import { Injectable, inject, signal, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, of, switchMap, tap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { LoginResponse, User } from '../models';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private platformId = inject(PLATFORM_ID);
  private isBrowser = isPlatformBrowser(this.platformId);

  private readonly TOKEN_KEY = 'admin_token';
  private readonly REFRESH_TOKEN_KEY = 'admin_refresh_token';
  private readonly USER_KEY = 'admin_user';

  currentUser = signal<User | null>(this.loadUser());

  login(email: string, password: string): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${environment.apis.identity}/auth/token`, { email, password })
      .pipe(
        // 1er pipe : erreurs HTTP uniquement
        tap(res => {
          if (!this.isBrowser) return;
          if (res.accessToken) localStorage.setItem(this.TOKEN_KEY, res.accessToken);
          if (res.refreshToken) localStorage.setItem(this.REFRESH_TOKEN_KEY, res.refreshToken);
          const user = res.user ?? { id: res.userId ?? '', email };
          localStorage.setItem(this.USER_KEY, JSON.stringify(user));
          this.currentUser.set(user);
        })
      )
      .pipe(
        // 2e pipe : vérification du rôle depuis response.accessToken directement
        switchMap(res => {
          if (!res.accessToken) return of(res);

          try {
            const payload = JSON.parse(atob(res.accessToken.split('.')[1]));
            const authorities: any[] = payload?.authorities ?? payload?.roles ?? [];
            const isAdmin = authorities.some((a: any) => {
              const r = typeof a === 'string' ? a : (a?.authority ?? a?.role ?? '');
              return r === 'ROLE_ADMIN' || r === 'ROLE_SUPER_ADMIN';
            });

            if (!isAdmin) {
              this.clearSession();
              return throwError(() => ({
                error: { message: 'Accès refusé. Cet espace est réservé aux administrateurs.' }
              }));
            }
          } catch {
            this.clearSession();
            return throwError(() => ({
              error: { message: 'Impossible de vérifier vos droits d\'accès.' }
            }));
          }

          return of(res);
        })
      );
  }

  clearSession(): void {
    if (!this.isBrowser) return;
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    this.currentUser.set(null);
  }

  logout(): void {
    if (this.isBrowser) {
      localStorage.removeItem(this.TOKEN_KEY);
      localStorage.removeItem(this.REFRESH_TOKEN_KEY);
      localStorage.removeItem(this.USER_KEY);
    }
    this.currentUser.set(null);
    this.router.navigate(['/login']);
  }

  getToken(): string | null {
    if (!this.isBrowser) return null;
    return localStorage.getItem(this.TOKEN_KEY);
  }

  getRefreshToken(): string | null {
    if (!this.isBrowser) return null;
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  /**
   * POST /auth/refresh : échange le refresh token contre un nouveau accessToken (et un
   * nouveau refreshToken, le backend fait tourner le refresh token à chaque appel — cf.
   * Auth.refreshToken côté identity-api). Utilisé par authInterceptor sur un 401.
   */
  refreshAccessToken(): Observable<LoginResponse> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return throwError(() => new Error('Aucun refresh token disponible'));
    }

    return this.http.post<LoginResponse>(`${environment.apis.identity}/auth/refresh`, { refreshToken })
      .pipe(
        tap(res => {
          if (!this.isBrowser) return;
          if (res.accessToken) localStorage.setItem(this.TOKEN_KEY, res.accessToken);
          if (res.refreshToken) localStorage.setItem(this.REFRESH_TOKEN_KEY, res.refreshToken);
        })
      );
  }

  isAuthenticated(): boolean {
    return this.isAdmin();
  }

  isAdmin(): boolean {
    const token = this.getToken();
    if (!token) return false;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload.exp && payload.exp * 1000 <= Date.now()) return false;
      const authorities: any[] = payload?.authorities ?? payload?.roles ?? [];
      return authorities.some((a: any) => {
        const r = typeof a === 'string' ? a : (a?.authority ?? a?.role ?? '');
        return r === 'ROLE_ADMIN' || r === 'ROLE_SUPER_ADMIN';
      });
    } catch { return false; }
  }

  isSuperAdmin(): boolean {
    const token = this.getToken();
    if (!token) return false;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload.exp && payload.exp * 1000 <= Date.now()) return false;
      const authorities: any[] = payload?.authorities ?? payload?.roles ?? [];
      return authorities.some((a: any) => {
        const r = typeof a === 'string' ? a : (a?.authority ?? a?.role ?? '');
        return r === 'ROLE_SUPER_ADMIN';
      });
    } catch { return false; }
  }

  /**
   * Droits accordés (claim `permissions` du JWT). Un super-admin a implicitement tous
   * les droits même si le claim ne les liste pas explicitement (le backend n'a besoin
   * d'interroger la table user_permissions pour aucun super-admin, cf. Auth.java).
   */
  getPermissions(): string[] {
    const token = this.getToken();
    if (!token) return [];
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload.exp && payload.exp * 1000 <= Date.now()) return [];
      return Array.isArray(payload?.permissions) ? payload.permissions : [];
    } catch { return []; }
  }

  hasPermission(code: string): boolean {
    if (this.isSuperAdmin()) return true;
    return this.getPermissions().includes(code);
  }

  private loadUser(): User | null {
    if (!isPlatformBrowser(this.platformId)) return null;
    try { return JSON.parse(localStorage.getItem(this.USER_KEY) ?? 'null'); } catch { return null; }
  }
}
